import { syncMemberChange } from '../members/memberSync';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import MyProfileHero from '../../pages/mypage/components/MyProfileHero';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { usersApi } from '../../api/users';
import useBadges from '../badges/useBadges';
import { shareOrCopyUrl } from '../../utils/share';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import { ReviewDraftProvider } from '../../pages/review/components/detail/ReviewDraftProtection';
import ReviewSafetyDialog from '../../pages/review/components/detail/ReviewSafetyDialog';
import { captureBadgeActor, isBadgeActorCurrent } from '../badges/progressEvents';

// Route+viewer keys in UserPage isolate pending dialogs and results per session.
export default function PublicProfileHeader({
  profile,
  onEdit,
  onFollowers,
  onFollowing,
  onUpdated,
}) {
  const viewer = useUserStore((s) => s.user),
    { locale } = useWebLocale(),
    en = locale === 'en';
  const navigate = useNavigate(),
    client = useQueryClient();
  const mine = viewer?.id != null && String(viewer.id) === String(profile.id);
  const badges = useBadges(profile.nickname);
  const [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(''),
    [safety, setSafety] = useState(false);
  const pending = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const active = () =>
    alive.current && String(useUserStore.getState().user?.id) === String(viewer?.id);
  const back = () =>
    window.history.state?.idx > 0 ? navigate(-1) : navigate('/explore', { replace: true });
  const follow = async () => {
    if (pending.current || mine) return;
    if (!viewer?.id || !localStorage.getItem('POSTPOP_AUTH_TOKEN')) {
      saveCurrentPathAndRedirectToLogin(navigate);
      return;
    }
    const actor = captureBadgeActor(),
      token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
    const isSessionCurrent = () =>
      active() &&
      (!actor || isBadgeActorCurrent(actor)) &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === token;
    pending.current = true;
    setBusy('follow');
    setNotice('');
    try {
      const response = await (profile.isFollowing ? usersApi.unfollowUser : usersApi.followUser)(
        profile.id,
        { isSessionCurrent, suppressGlobalError: true },
      );
      if (!isSessionCurrent()) return;
      const updated = response.data;
      if (
        !updated ||
        String(updated.id) !== String(profile.id) ||
        typeof updated.isFollowing !== 'boolean'
      )
        throw Error('Invalid follow response');
      const synced = await syncMemberChange(client, {
        viewer,
        target: { ...profile, ...updated },
        kind: 'follow',
        current: isSessionCurrent,
      });
      if (synced && isSessionCurrent()) onUpdated((previous) => ({ ...previous, ...updated }));
    } catch {
      if (isSessionCurrent())
        setNotice(
          en
            ? 'Could not update following. Try again.'
            : '팔로우 상태를 변경하지 못했어요. 다시 시도해 주세요.',
        );
    } finally {
      pending.current = false;
      if (active()) setBusy('');
    }
  };
  const share = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy('share');
    setNotice('');
    try {
      const result = await shareOrCopyUrl({
        url: `https://postpop.kr/users/by-id/${profile.id}`,
        title: `${profile.nickname} — postpop`,
        text: en
          ? `${profile.nickname}’s music log on Postpop`
          : `${profile.nickname}님의 음악 기록`,
        event: { targetUserId: profile.id, surface: 'PROFILE', section: 'SHARE' },
      });
      if (active() && result === 'copied') setNotice(en ? 'Link copied.' : '링크를 복사했어요.');
    } catch {
      if (active())
        setNotice(en ? 'Could not share. Try again.' : '공유하지 못했어요. 다시 시도해 주세요.');
    } finally {
      pending.current = false;
      if (active()) setBusy('');
    }
  };
  const openFollowList = (open) => () => {
    if (!viewer?.id || !localStorage.getItem('POSTPOP_AUTH_TOKEN')) {
      saveCurrentPathAndRedirectToLogin(navigate);
      return;
    }
    open?.();
  };
  const assertAccount = () => {
    if (!active() || !viewer?.id || mine) throw Error('Profile session changed');
  };
  const marked = badges.data
    ? {
        ...profile,
        profileMarkType: badges.data.profileMarkType,
        featuredBadge: badges.data.featuredBadge,
        sponsorshipMark: badges.data.sponsorshipMark?.isSupporter
          ? badges.data.sponsorshipMark
          : null,
      }
    : profile;
  return (
    <ReviewDraftProvider en={en}>
      <MyProfileHero
        publicProfile
        profile={marked}
        onBack={back}
        onEdit={mine ? onEdit : undefined}
        onShare={share}
        shareDisabled={busy === 'share'}
        onFollowers={openFollowList(onFollowers)}
        onFollowing={openFollowList(onFollowing)}
        primaryAction={
          mine
            ? undefined
            : {
                active: profile.isFollowing,
                disabled: busy === 'follow',
                label:
                  busy === 'follow'
                    ? en
                      ? 'Working…'
                      : '처리 중'
                    : profile.isFollowing
                      ? en
                        ? 'Following'
                        : '팔로우 중'
                      : en
                        ? 'Follow'
                        : '팔로우',
                onPress: follow,
              }
        }
        onSafety={!mine && viewer?.id ? () => setSafety(true) : undefined}
      />
      {notice && <p role="status">{notice}</p>}
      {safety && (
        <ReviewSafetyDialog
          target={{
            contentType: 'USER',
            contentId: profile.id,
            reportedUserId: profile.id,
            displayName: profile.nickname,
          }}
          en={en}
          assertAccount={assertAccount}
          onClose={() => setSafety(false)}
          onNotice={setNotice}
          onBlocked={back}
        />
      )}
    </ReviewDraftProvider>
  );
}
